import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  CardBody, 
  CardHeader,
  Button, 
  Alert,
  Spinner,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  Input,
  InputGroup,
  Label
} from 'reactstrap';
import { apiKeyApi, ApiTokenData } from '../../helpers/api_key_helper';
import BreadCrumb from '../../Components/Common/BreadCrumb';

const ApiKeysManagement: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [rotateLoading, setRotateLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [apiTokens, setApiTokens] = useState<ApiTokenData | null>(null);
  const [showRotateModal, setShowRotateModal] = useState<boolean>(false);
  const [showTokens, setShowTokens] = useState<{current: boolean, rotated: boolean}>({
    current: false,
    rotated: false
  });

  useEffect(() => {
    loadApiTokens();
  }, []);

  const loadApiTokens = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiKeyApi.getApiTokens();
      console.log('API tokens response:', response);
      
      if (response.status === 200) {
        setApiTokens(response.data);
      } else {
        setError(response.data?.message || 'Failed to load API tokens');
      }
    } catch (err: any) {
      console.error('Error loading API tokens:', err);
      // Handle 403 specifically for admin access
      if (err.response?.status === 403) {
        setError(err.response?.data?.message || 'Access denied. Admin role required.');
      } else {
        setError(err.response?.data?.message || 'Failed to load API tokens');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRotateToken = async () => {
    try {
      setRotateLoading(true);
      setError(null);
      setSuccess(null);
      
      const response = await apiKeyApi.rotateApiToken();
      console.log('Rotate token response:', response);
      
      if (response.status === 200) {
        setSuccess('API token rotated successfully! The new token is now available.');
        setShowRotateModal(false);
        // Reload the tokens to show updated data
        await loadApiTokens();
      } else {
        setError(response.data?.message || 'Failed to rotate API token');
      }
    } catch (err: any) {
      console.error('Error rotating API token:', err);
      setError(err.response?.data?.message || 'Failed to rotate API token');
    } finally {
      setRotateLoading(false);
    }
  };

  const copyToClipboard = async (text: string, tokenType: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccess(`${tokenType} token copied to clipboard`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      setError('Failed to copy to clipboard');
    }
  };

  const formatToken = (token: string, isVisible: boolean): string => {
    if (!token) return '';
    if (isVisible) return token;
    return token.substring(0, 8) + '...' + token.substring(token.length - 8);
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const getExpirationDate = (rotationDate: string | null): string => {
    if (!rotationDate) return '';
    const rotation = new Date(rotationDate);
    const expiration = new Date(rotation);
    expiration.setDate(expiration.getDate() + 15);
    return expiration.toLocaleDateString();
  };

  document.title = "API Token | Velzon - React Admin & Dashboard Template";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="API Token" pageTitle="Admin" />
          
          {error && (
            <Alert color="danger" className="mb-4">
              <i className="ri-error-warning-line me-2"></i>
              {error}
            </Alert>
          )}

          {success && (
            <Alert color="success" className="mb-4">
              <i className="ri-checkbox-circle-line me-2"></i>
              {success}
            </Alert>
          )}

          <Row>
            <Col xs="12">
              <Card>
                <CardHeader>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h5 className="card-title mb-0">API Token Management</h5>
                      <p className="text-muted mb-0">Manage your company's API authentication token</p>
                    </div>
                    <Button 
                      color="warning" 
                      onClick={() => setShowRotateModal(true)}
                      className="d-flex align-items-center gap-2"
                      disabled={loading}
                    >
                      <i className="ri-refresh-line"></i>
                      Rotate Token
                    </Button>
                  </div>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <div className="text-center py-4">
                      <Spinner color="primary" className="me-2" />
                      Loading API tokens...
                    </div>
                  ) : apiTokens ? (
                    <div className="row g-4">
                      {/* Current API Token */}
                      <div className="col-12">
                        <Card className="border">
                          <CardBody>
                            <div className="d-flex justify-content-between align-items-start mb-3">
                              <div>
                                <h6 className="mb-1">
                                  <i className="ri-key-line me-2 text-primary"></i>
                                  Current API Token
                                </h6>
                                {apiTokens.apiTokenRotated && apiTokens.apiTokenRotatedDate ? (
                                  <Badge color="warning" className="mb-2">Expiring</Badge>
                                ) : (
                                  <Badge color="success" className="mb-2">Active</Badge>
                                )}
                                <p className="text-muted mb-1 small">
                                  Primary token for API authentication
                                </p>
                                {apiTokens.apiTokenRotated && apiTokens.apiTokenRotatedDate && (
                                  <p className="text-warning mb-0 small">
                                    <i className="ri-time-line me-1"></i>
                                    <strong>Expires on {getExpirationDate(apiTokens.apiTokenRotatedDate)}</strong>
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="mb-2">
                              <Label className="form-label small text-muted">Token Value</Label>
                              <InputGroup>
                                <Input
                                  type="text"
                                  value={formatToken(apiTokens.apiToken, showTokens.current)}
                                  readOnly
                                  className="font-monospace"
                                />
                                <Button
                                  color="outline-secondary"
                                  onClick={() => setShowTokens(prev => ({...prev, current: !prev.current}))}
                                  title={showTokens.current ? "Hide token" : "Show token"}
                                >
                                  <i className={`ri-eye-${showTokens.current ? 'off-' : ''}line`}></i>
                                </Button>
                                <Button
                                  color="outline-primary"
                                  onClick={() => copyToClipboard(apiTokens.apiToken, 'Current')}
                                  title="Copy to clipboard"
                                >
                                  <i className="ri-clipboard-line"></i>
                                </Button>
                              </InputGroup>
                            </div>
                          </CardBody>
                        </Card>
                      </div>

                      {/* Rotated API Token */}
                      <div className="col-12">
                        <Card className="border">
                          <CardBody>
                            <div className="d-flex justify-content-between align-items-start mb-3">
                              <div>
                                <h6 className="mb-1">
                                  <i className="ri-key-2-line me-2 text-warning"></i>
                                  Rotated API Token
                                </h6>
                                {apiTokens.apiTokenRotated ? (
                                  <>
                                    <Badge color="warning" className="mb-2">Available</Badge>
                                    <p className="text-muted mb-0 small">
                                      New token created on {formatDate(apiTokens.apiTokenRotatedDate)}
                                    </p>
                                  </>
                                ) : (
                                  <>
                                    <Badge color="secondary" className="mb-2">None</Badge>
                                    <p className="text-muted mb-0 small">
                                      No rotated token available. Click "Rotate Token" to generate a new one.
                                    </p>
                                  </>
                                )}
                              </div>
                            </div>
                            {apiTokens.apiTokenRotated && (
                              <div className="mb-2">
                                <Label className="form-label small text-muted">Token Value</Label>
                                <InputGroup>
                                  <Input
                                    type="text"
                                    value={formatToken(apiTokens.apiTokenRotated, showTokens.rotated)}
                                    readOnly
                                    className="font-monospace"
                                  />
                                  <Button
                                    color="outline-secondary"
                                    onClick={() => setShowTokens(prev => ({...prev, rotated: !prev.rotated}))}
                                    title={showTokens.rotated ? "Hide token" : "Show token"}
                                  >
                                    <i className={`ri-eye-${showTokens.rotated ? 'off-' : ''}line`}></i>
                                  </Button>
                                  <Button
                                    color="outline-primary"
                                    onClick={() => copyToClipboard(apiTokens.apiTokenRotated!, 'Rotated')}
                                    title="Copy to clipboard"
                                  >
                                    <i className="ri-clipboard-line"></i>
                                  </Button>
                                </InputGroup>
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      </div>

                      {/* Information Card */}
                      <div className="col-12">
                        <Alert color="info" className="mb-0">
                          <h6 className="alert-heading">
                            <i className="ri-information-line me-2"></i>
                            API Token Usage
                          </h6>
                          <div className="mb-3">
                            <strong>Authentication:</strong> Include your API token in the Authorization header as "Bearer YOUR_TOKEN"
                          </div>
                          <div className="mb-3">
                            <strong>Token Rotation:</strong> When you rotate tokens, both the current and rotated tokens will work simultaneously for 15 days, allowing you to update your integrations without downtime. The current token will expire 15 days after rotation.
                          </div>
                          <div className="mb-0">
                            <strong>Security:</strong> Keep your tokens secure and rotate them regularly. Never expose tokens in client-side code or public repositories.
                          </div>
                        </Alert>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="avatar-lg mx-auto mb-4">
                        <div className="avatar-title bg-light text-muted rounded-circle">
                          <i className="ri-key-line fs-24"></i>
                        </div>
                      </div>
                      <h5 className="mb-2">No API Tokens Available</h5>
                      <p className="text-muted">Unable to load API token information</p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Rotate Token Confirmation Modal */}
      <Modal isOpen={showRotateModal} toggle={() => setShowRotateModal(false)} size="md" centered>
        <ModalHeader toggle={() => setShowRotateModal(false)}>
          <i className="ri-refresh-line text-warning me-2"></i>
          Confirm Token Rotation
        </ModalHeader>
        <ModalBody>
          <div className="text-center">
            <div className="avatar-lg mx-auto mb-4">
              <div className="avatar-title bg-soft-warning text-warning rounded-circle">
                <i className="ri-refresh-line fs-24"></i>
              </div>
            </div>
            <h5 className="mb-3">Rotate API Token?</h5>
            <p className="text-muted mb-4">
              This will generate a new API token and save it as the "rotated token". 
              Both your current token and the new rotated token will be valid for API authentication.
            </p>
            <div className="alert alert-warning text-start">
              <strong>Important:</strong>
              <ul className="mb-0 mt-2">
                <li>Your current token will continue to work for <strong>15 days</strong> after rotation</li>
                <li>The new rotated token will be valid immediately</li>
                <li>This allows zero-downtime token updates</li>
                <li><strong>Plan your migration within 15 days</strong> - the current token will expire after that</li>
                <li>Update your applications to use the new token before the current one expires</li>
              </ul>
            </div>
            
            {error && (
              <Alert color="danger" className="mt-3">
                <i className="ri-error-warning-line me-2"></i>
                {error}
              </Alert>
            )}
          </div>
          
          <div className="d-flex justify-content-center gap-2 mt-4">
            <Button 
              type="button" 
              color="secondary" 
              onClick={() => setShowRotateModal(false)}
              disabled={rotateLoading}
            >
              Cancel
            </Button>
            <Button 
              type="button" 
              color="warning"
              onClick={handleRotateToken}
              disabled={rotateLoading}
            >
              {rotateLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Rotating...
                </>
              ) : (
                <>
                  <i className="ri-refresh-line me-2"></i>
                  Rotate Token
                </>
              )}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default ApiKeysManagement;